<template>
  <ontos-form-dialog v-bind="heading" max-width="800px"
                     :saving="saving" :save-disabled="edit.unchanged" :dirty="edit.dirty"
                     :full-page-route="fullPageRoute"
                     @save="save()" @closed="$emit('closed')">
    <v-form ref="form">
      <!-- alias fixo só na edição: ali ele é a chave da coleção na URL, nos documentos e
           nos relacionamentos que apontam para ela. Ao criar precisa ser editável, com
           sugestão automática a partir do nome. -->
      <ontos-class-input ref="input" :value="draft" @input="draft = $event" :ideas="workspace.ideas"
                         :fixed-alias="!isCreating"
                         :start-with-auto-alias="isCreating" :start-with-auto-plural="isCreating"/>
      <!-- Só ao criar: depois a chave se mexe pelo modal de propriedade -->
      <v-checkbox v-if="isCreating" v-model="identifiedByName" hide-details class="mt-0"
                  :label="$tt('identified-by-name')"/>
    </v-form>
  </ontos-form-dialog>
</template>
<script lang="ts">
import {Component, Vue, Prop, toNative} from 'vue-facing-decorator'
import ClassInput from '@/components/design/class/ClassInput.vue'
import Idea from '@/types/Idea'
import IdeaProperty from '@/types/IdeaProperty'
import {EnhancedWorkpace} from '@/types/Workspace'
import {deepCopy} from '@/utils/copy'
import {DraftState, draftState, emptyIdea, nameProperty} from '@/utils/modelUtils'
import {Heading, collectionCreate, collectionEdit} from '@/utils/heading'

/**
 * Cria e edita a coleção sem sair do design. Casca de modal sobre o mesmo ClassInput da
 * página, como os modais de propriedade e de fluxo.
 *
 * Sem a prop `idea` é criação, com ela é edição — o mesmo par que o WorkflowDialog usa.
 * A página `create-idea` continua existindo e vira o "abrir em página inteira" do modal.
 *
 * Quem persiste é o host, que é um WorkspaceRouteComponent e tem a atualização reativa
 * do modelo em memória.
 */
@Component({emits: ['closed', 'create', 'save'], 
  name: 'ontos-class-dialog',
  components: {'ontos-class-input': ClassInput}
})
class ClassDialog extends Vue {
  /** undefined = criando; objeto = editando aquela coleção */
  @Prop({default: undefined}) readonly idea!: Idea | undefined
  @Prop({required: true}) readonly workspace!: EnhancedWorkpace
  @Prop({type: Boolean, default: false}) readonly saving!: boolean

  draft: Idea = emptyIdea()
  /** Ponto de partida do rascunho ao criar, para saber se há o que perder ao sair */
  seed: Idea = emptyIdea()

  /**
   * No created, e não no inicializador do campo: o inicializador roda sobre a instância
   * crua da classe, onde as props ainda não existem — `this.idea` viria undefined e
   * EDITAR uma coleção começaria do modelo vazio.
   *
   * deepCopy na edição: editar o objeto do workspace direto mostraria o rascunho atrás
   * do modal.
   */
  created() {
    if (this.idea) this.draft = deepCopy(this.idea)
    this.seed = deepCopy(this.draft)
  }
  identifiedByName = true

  get isCreating(): boolean {
    return !this.idea
  }

  /** O mesmo cabeçalho da página de criar: a regra mora no utils/heading */
  get heading(): Heading {
    return this.isCreating ? collectionCreate(this.workspace) : collectionEdit(this.idea!)
  }

  /** A coleção que ainda não existe não tem página própria: a de criar é que serve */
  get fullPageRoute() {
    if (!this.isCreating) return null
    return {name: 'create-idea', params: {system: this.workspace.alias}}
  }

  /** O que mudou, se o salvar habilita e se sair pergunta — a regra mora no `draftState` */
  get edit(): DraftState {
    return draftState(this.idea, this.draft, this.seed)
  }

  /**
   * A coleção nasce com um atributo Nome que é a chave, se o usuário quiser. É o que a
   * página de criar já fazia: sem nenhuma propriedade marcada, o identificador de uma
   * instância cai no `Coleção #n`.
   */
  get nameProperty(): IdeaProperty {
    return nameProperty()
  }

  save() {
    const input = this.$refs.input as any
    if (!input || !input.validateModel()) return
    if (this.isCreating) {
      this.$emit('create', {...this.draft, properties: this.identifiedByName ? [this.nameProperty] : []})
    } else {
      this.$emit('save', this.edit.changes)
    }
  }

  /** O host repassa o erro de unicidade para o campo que colidiu */
  notUnique(property: 'name' | 'pluralName' | 'alias') {
    const input = this.$refs.input as any
    if (input) input.notUnique(property)
  }
}

export default toNative(ClassDialog)
</script>
